import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { User, defaultUser } from '../models/User';
import { Outlet } from 'react-router';
import useFetch from '../hooks/useFetch';
import { userProfilePath } from '../config/apiRoutes';
import AuthContext, { AuthState } from './AuthContext';
import { Box } from '@mui/material';
import { ShardLoading } from '../styles/loading';
import { RestaurantSetting } from '../models/RestaurantSetting';
import { useTranslation } from 'react-i18next';
import { getCurrentFeatures, getCurrentSubscription } from '../models/RestaurantSubscription';
import { useLocalStorage } from '../hooks/useLocalStorage';
import NotActivatedPage from '../pages/NotActivatedPage';
import AcceptTermsPage from '../pages/AcceptTermsPage';

type Props = {}

export interface UserContextProps {
    user: User;
    selectedRestaurant: RestaurantSetting | null;
    setSelectedRestaurantShortName: (shortName: string) => void;
    restaurantSettings: RestaurantSetting[];
    currentSubscription: ReturnType<typeof getCurrentSubscription> | undefined;
    hasFeature: (feature: string) => boolean;
    refresh: () => Promise<void>;
}

const UserContext = createContext<UserContextProps>({} as any);

export const UserProvider = (props: Props) => {
    const auth = useContext(AuthContext);
    const [user, setUser] = useState<User>(defaultUser);
    const userLoader = useFetch(true);
    const refreshLoader = useFetch(false);
    const [selectedRestaurantShortName, setSelectedRestaurantShortName] = useLocalStorage<string>("", "selectedRestaurantShortName");
    const { t } = useTranslation();
    const restaurantSettings = useMemo(() => {
        const allSettings = user.roles
            .filter(role => role.restaurantSetting != null)
            .map(role => role.restaurantSetting) as RestaurantSetting[];
        const uniqueShortNames = new Set(allSettings.map(setting => setting.restaurantShortName));
        return Array.from(uniqueShortNames).map(shortName => {
            return allSettings.find(setting => setting.restaurantShortName === shortName)!;
        });
    }, [user.roles]);
    const selectedRestaurant = useMemo(() => {
        if (selectedRestaurantShortName == "") return null;
        return restaurantSettings.find(restaurant => restaurant.restaurantShortName === selectedRestaurantShortName) || null;
    }, [selectedRestaurantShortName, restaurantSettings])
    const currentSubscription = useMemo(() => {
        if (selectedRestaurant == null) return undefined;
        return getCurrentSubscription(selectedRestaurant);
    }, [selectedRestaurant]);
    const isRegitrationFinished = useMemo(() => {
        if (userLoader.loading || userLoader.error) return false;
        if (user.isActivated == false) return false;
        if (user.termsAccepted == false) return false;
        return true;
    }, [userLoader.loading, userLoader.error, user])
    const showTermsPage = useMemo(() => {
        if (userLoader.loading || userLoader.error) return false;
        return user.termsAccepted == false;
    }, [userLoader.loading, userLoader.error, user])
    const showActivationPage = useMemo(() => {
        if (userLoader.loading || userLoader.error) return false;
        if (user.termsAccepted == false) return false;
        return user.isActivated == false;
    }, [userLoader.loading, userLoader.error, user])

    useEffect(() => {
        if (auth.authState === AuthState.LOGGED_IN && auth.user != null) {
            initUser();
        }
    }, [auth.authState, auth.user]);

    useEffect(() => {
        if (restaurantSettings.length > 0) {
            if (selectedRestaurantShortName === "") {
                setSelectedRestaurantShortName(restaurantSettings[0].restaurantShortName);
            } else {
                const foundRestaurant = restaurantSettings.find(restaurant => restaurant.restaurantShortName === selectedRestaurantShortName);
                if (foundRestaurant == null) {
                    setSelectedRestaurantShortName(restaurantSettings[0].restaurantShortName);
                }
            }
        }
    }, [restaurantSettings]);

    async function initUser() {
        try {
            const response = await userLoader.call<undefined, User>("GET", userProfilePath);
            if (response.ok && response.data) {
                setUser(response.data);
            } else {
                if (response.status === 401) {
                    auth.setErrorMessage(t("auth.missing_perrmissions"));
                } else {
                    auth.setErrorMessage(t("common.internal_server_error"));
                }
                auth.logout();
            }
        } catch {
            auth.setErrorMessage(t("common.internal_server_error"));
            auth.logout();
        }
    }

    async function refresh() {
        try {
            const response = await refreshLoader.call<undefined, User>("GET", userProfilePath);
            if (response.ok && response.data) {
                setUser(response.data);
            }
        } catch {
            auth.setErrorMessage(t("common.internal_server_error"));
        }
    }

    function hasFeature(feature: string) {
        if (selectedRestaurant == null) return false;
        if (currentSubscription == null) return false;
        return getCurrentFeatures(selectedRestaurant).some(f => f === feature);
    }

    const state = {
        user,
        selectedRestaurant,
        setSelectedRestaurantShortName,
        restaurantSettings,
        currentSubscription,
        hasFeature,
        refresh: refresh
    }

    return (
        <UserContext.Provider value={state}>
            {isRegitrationFinished && (
                <Outlet />
            )}
            {userLoader.loading && (
                <Box pt={8} px={4} textAlign={"center"}>
                    <ShardLoading size="60px" />
                </Box>
            )}
            {showTermsPage && (
                <AcceptTermsPage />
            )}
            {showActivationPage && (
                <NotActivatedPage />
            )}
        </UserContext.Provider>
    )
}

export default UserContext